import React from 'react'
import PropTypes from 'prop-types'

/**
 * PlayerStatsTable Component
 *
 * Displays player statistics including games played, goals, assists, points, and penalty minutes.
 * Used in LeagueDetails (Standings tab), Home (stats leaderboard), and Dashboard (player view).
 *
 * @param {Object} props
 * @param {Array} props.stats - Array of player statistics
 * @param {boolean} [props.showTeamInfo=true] - Display team column with color indicator
 * @param {boolean} [props.compact=false] - Use compact view with fewer columns
 * @param {number} [props.limit] - Limit number of rows displayed
 * @param {string} [props.title=''] - Optional title for the table
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Function} [props.onPlayerClick] - Callback when player row is clicked
 * @param {Array} [props.highlightPlayerIds=[]] - Player IDs to highlight
 */
const PlayerStatsTable = React.memo(({
  stats = [],
  showTeamInfo = true,
  compact = false,
  limit,
  title = '',
  className = '',
  onPlayerClick,
  highlightPlayerIds = []
}) => {
  if (!stats || stats.length === 0) {
    return null
  }

  const displayStats = limit ? stats.slice(0, limit) : stats

  return (
    <div className={className}>
      {title && <h4 className="font-semibold text-slate-900 mb-3">{title}</h4>}
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              {showTeamInfo && <th>Team</th>}
              {!compact && (
                <th className="text-center hidden md:table-cell">
                  {showTeamInfo ? 'Jersey' : 'No.'}
                </th>
              )}
              {!compact && (
                <th className="text-center hidden lg:table-cell">GP</th>
              )}
              <th className="text-center">G</th>
              <th className="text-center">A</th>
              <th className="text-center">PTS</th>
              {!compact && (
                <th className="text-center hidden sm:table-cell">PIM</th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayStats.map((stat, index) => {
              const isHighlighted = highlightPlayerIds.includes(stat.player_id)
              const playerName = stat.player_name || stat.name
              const teamName = stat.team_name
              const teamColor = stat.team_color

              return (
                <tr
                  key={stat.player_id || index}
                  className={`${
                    isHighlighted ? 'bg-ice-50' : ''
                  } ${onPlayerClick ? 'cursor-pointer' : ''}`}
                  onClick={onPlayerClick ? () => onPlayerClick(stat) : undefined}
                >
                  <td className="font-semibold text-slate-900">{index + 1}</td>
                  <td className="font-medium text-slate-900">
                    {playerName}
                    {isHighlighted && <span className="ml-1 text-ice-600 text-xs font-semibold">(You)</span>}
                  </td>
                  {showTeamInfo && (
                    <td>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: teamColor || '#94a3b8' }}
                        />
                        <span className="text-slate-700">{teamName}</span>
                      </div>
                    </td>
                  )}
                  {!compact && (
                    <td className="text-center hidden md:table-cell text-slate-600">
                      {stat.jersey_number || '-'}
                    </td>
                  )}
                  {!compact && (
                    <td className="text-center hidden lg:table-cell text-slate-600">
                      {stat.games_played}
                    </td>
                  )}
                  <td className="text-center text-slate-700">{stat.goals}</td>
                  <td className="text-center text-slate-700">{stat.assists}</td>
                  <td className="text-center font-bold text-ice-600">{stat.points}</td>
                  {!compact && (
                    <td className="text-center hidden sm:table-cell text-slate-600">
                      {stat.penalty_minutes}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
})

PlayerStatsTable.displayName = 'PlayerStatsTable'

PlayerStatsTable.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    player_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    player_name: PropTypes.string,
    name: PropTypes.string,
    team_name: PropTypes.string,
    team_color: PropTypes.string,
    jersey_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    games_played: PropTypes.number,
    goals: PropTypes.number,
    assists: PropTypes.number,
    points: PropTypes.number,
    penalty_minutes: PropTypes.number,
  })),
  showTeamInfo: PropTypes.bool,
  compact: PropTypes.bool,
  limit: PropTypes.number,
  title: PropTypes.string,
  className: PropTypes.string,
  onPlayerClick: PropTypes.func,
  highlightPlayerIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
}

export default PlayerStatsTable
